import { useCallback, useState } from 'react';
import { CalendarGrid, CalendarGridRenderProp, AllDayCalendarEvent } from '@frontend/calendar-grid';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { useModalControl } from '@frontend/design-system';
import {
  renderGridEventCard,
  renderGridEventTooltip,
} from '../../components/calendar-view/events-cards/grid-event-card-switch';
import { useColumnEntityReorderStore } from '../../components/calendar-view/providers';
import { DEFAULT_ZOOM_LEVEL } from '../../components/calendar-view/zoom/zoom-context';
import { OutOfOfficeEventCardModal } from '../../components/modals/out-of-office-event-card-modal';
import { useCalendarGridColumnReorder } from '../../hooks/scheduler-v3/use-calendar-grid-column-reorder';
import { getProviderName, useCalendarGridV2Data } from '../../hooks/use-calendar-grid-v2-data';
import { useCalendarStore } from '../../stores/calendar.store';
import { useSetCalendarGridEventsAsSelected } from '../../stores/schedule-appointment-info-panel.store';
import { useCalendarGridAutoScroll } from '../CalendarV3/components/use-calendar-grid-auto-scroll';
import { OnboardingFlowEmptyCalendarComponent } from '../CalendarV3/onboarding-flow/onboarding-flow-empty-calendar-component';
import { CalendarTimeLineViewV2Props } from './CalendarTimeLineViewV2';

const NoEventsComponentAdapter = () => <OnboardingFlowEmptyCalendarComponent />;

type CalendarV2WithGridProps = CalendarTimeLineViewV2Props;

export const CalendarV2WithGrid = (props: CalendarV2WithGridProps) => {
  const { t } = useTranslation('schedule');
  const calendarData = useCalendarGridV2Data(props);
  const { calendarDate, calendarEntityType } = useCalendarStore(['calendarDate', 'calendarEntityType']);
  const { getScopeName } = useAppScopeStore();
  const [calendarSettings, setCalendarSettings] = usePreference('ScheduleCalendarSettings');
  const zoomLevel = calendarSettings?.zoomLevel ?? DEFAULT_ZOOM_LEVEL;
  const { handleEventsSelect, selectedEventIds, deepLinkedEventId } = useSetCalendarGridEventsAsSelected(calendarDate);
  const { modalProps, openModal } = useModalControl();
  const [allDayEventDetails, setAllDayEventDetails] = useState<{
    id: string;
    title: string;
    startDate: string;
    endDate: string;
    locationId: string;
    providerId?: string;
    providerName?: string;
    locationName?: string;
  } | null>(null);

  const handleAllDayEventClick = useCallback(
    (event: AllDayCalendarEvent) => {
      const providerId = event.columnId;
      const providerInfo = props.filteredProviderList.find((provider) => provider.id === providerId);
      const providerName = providerInfo ? getProviderName(providerInfo) : '';

      setAllDayEventDetails({
        id: event.id,
        title: event.name,
        startDate: event.startDate || calendarDate,
        endDate: event.endDate || calendarDate,
        locationId: event.groupId,
        providerId,
        providerName,
        locationName: getScopeName(event.groupId),
      });
      openModal();
    },
    [calendarDate, getScopeName, openModal, props.filteredProviderList]
  );

  // Column reorder support — pass base columnGroups from data, get back merged version
  const { onColumnReorder, columnGroups } = useCalendarGridColumnReorder(
    calendarEntityType,
    calendarData.columnGroups ?? []
  );
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);

  // Auto-scroll to the appropriate position after the calendar finishes loading
  const { wrapperRef } = useCalendarGridAutoScroll({
    events: calendarData.events,
    isLoading: props.isLoading,
    calendarDate,
    zoomLevel,
  });

  const renderEmptyStateComponent = useCallback(
    (_groupId: string[], variant: 'no-events' | 'no-columns') => {
      if (props.isLoading) return null;

      if (variant === 'no-events') return null;

      return <NoEventsComponentAdapter />;
    },
    [props.isLoading]
  );

  return (
    <>
      <CalendarGrid
        {...calendarData}
        columnGroups={columnGroups}
        selectedEventIds={selectedEventIds}
        onEventsSelect={handleEventsSelect}
        renderEventCard={renderGridEventCard as CalendarGridRenderProp}
        renderEventTooltip={renderGridEventTooltip as CalendarGridRenderProp}
        onColumnReorder={onColumnReorder}
        isColumnReorderActive={isInReorderMode}
        deepLinkedEventId={deepLinkedEventId}
        renderEmptyStateComponent={renderEmptyStateComponent}
        onAllDayEventClick={handleAllDayEventClick}
        wrapperRef={wrapperRef}
        zoomConfig={{
          levels: [1, 2],
          zoomLevel,
          onZoomLevelChange: (level: number) => setCalendarSettings({ ...calendarSettings, zoomLevel: level }),
          showControls: true,
        }}
      />
      {allDayEventDetails && (
        <OutOfOfficeEventCardModal
          id={allDayEventDetails.id}
          eventType='outOfOffice'
          title={allDayEventDetails.title}
          duration={t('All day')}
          locationId={allDayEventDetails.locationId}
          locationName={allDayEventDetails.locationName}
          providerId={allDayEventDetails.providerId}
          providerName={allDayEventDetails.providerName}
          startDate={allDayEventDetails.startDate}
          endDate={allDayEventDetails.endDate}
          calendarDate={calendarDate}
          modalProps={modalProps}
        />
      )}
    </>
  );
};
